import { appAlertComponentModule } from '../../components/app-alert/app-alert';
import { multiFilePathDialogComponentModule } from '../../components/multi-file-path-dialog/multi-file-path-dialog';
import { multiFileOverwriteDialogComponentModule } from '../../components/multi-file-overwrite-dialog/multi-file-overwrite-dialog';
import { isFileEvent, stopEvent } from '../../utils/event-utils';
import { languages } from '../../constants/languages';
import {
  MAX_FILE_SIZE,
  MAX_IMPORT_FILES,
  SESSION_LOG_FILENAME,
  SESSION_LOG_MAX_FILE_SIZE,
} from './multi-file-challenge-service-constants';
import * as gitIgnoreParser from '../../../../../node_modules/@gerhobbelt/gitignore-parser/lib/index';
import { isImageFile } from '../../utils/file-utils/image-file-utils';

import './multi-file-challenge-service-drag-and-drop.less';
import { isFunction } from '../../utils/type-utils';

const TOO_MANY_FILES = {};

// exclude some common directories from ever being supported on drag-and-drop uploads
const ALWAYS_EXCLUDE = /(^|\/)\.(git|hg|svn|idea|vscode)(\/|$)/;

// Raw AI session logs are the one file type allowed past the 1 MB limit; see
// SESSION_LOG_MAX_FILE_SIZE for why the exemption is this narrow. Matched on the
// basename so a nested drop is treated the same as a top-level one.
export function maxSizeFor(file) {
  const name = (file.relativePath || file.name || '').split('/').pop();
  return SESSION_LOG_FILENAME.test(name)
    ? SESSION_LOG_MAX_FILE_SIZE
    : MAX_FILE_SIZE;
}

export function formatSizeLimit(bytes) {
  return `${Math.round(bytes / 1048576)} MB`;
}

export const multiFileChallengeServiceDragAnDropModule = angular
  .module('Q.services.multi-file-challenge-service-dnd', [
    appAlertComponentModule,
    multiFilePathDialogComponentModule,
    multiFileOverwriteDialogComponentModule,
  ])

  .factory(
    'multiFileChallengeServiceDND',
    function (
      appAlert,
      $window,
      $document,
      $timeout,
      $q,
      $log,
      multiFilePathDialog,
      multiFileOverwriteDialog,
    ) {
      let dragFileOverEl;

      function getFileContents(file) {
        if (!$window.FileReader) return $q.reject('No File Reader Model');
        return $q((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (evt) => {
            resolve(evt.target.result);
          };
          reader.onerror = reader.onabort = reject;
          if (isImageFile(file)) {
            reader.readAsDataURL(file);
          } else {
            reader.readAsText(file);
          }
        });
      }

      function setupDragAndDrop(svc, _createFile) {
        let dragOverShowing = false;
        let dragExitTimer;

        /**
         * Sets up the drag over event, along with a nice visual drop guide, if this is a file event
         * @param event
         */
        function handleDragOver(event) {
          if (!isFileEvent(event) || svc.expired) return;
          stopEvent(event);
          if (!dragFileOverEl) {
            dragFileOverEl = angular.element(
              '<div class="multi-file-challenge-drag-over"><span>Drop files to import</span></div>',
            );
            $document.find('body').append(dragFileOverEl);
          }
          $timeout.cancel(dragExitTimer);
          if (!dragOverShowing) {
            dragOverShowing = true;
            dragFileOverEl.addClass('active-setup');
            $timeout(
              () => {
                dragFileOverEl.addClass('active');
              },
              1,
              false,
            );
          }
        }

        /**
         * Clears the visual drop guide if this is a file event
         * @param event
         */
        function handleDragExit(event) {
          if (!isFileEvent(event)) return;
          stopEvent(event);
          if (dragFileOverEl) {
            dragExitTimer = $timeout(
              () => {
                dragFileOverEl.removeClass('active');
                dragExitTimer = $timeout(
                  () => {
                    dragFileOverEl.removeClass('active-setup');
                    dragOverShowing = false;
                  },
                  300,
                  false,
                );
              },
              250,
              false,
            );
          }
        }

        const safeMimeTypes =
          /^(text\/|application\/(x-)?(ecmascript|javascript|json|typescript|xhtml|xml|yaml|(script\.)?.{0,2}sh$)|image\/(svg|png|jpeg))/;

        /**
         * Allows dropping files & directories from the file system
         * @param event
         */
        function handleDrop(event) {
          if (!isFileEvent(event) || svc.expired) return;
          // always stop the event here
          handleDragExit(event);

          const dt = event.dataTransfer;
          if (dt) {
            if (dt.items) {
              const allFiles = Array.from(dt.items)
                .filter((item) => item.webkitGetAsEntry || item.kind === 'file')
                .map((item) => {
                  const webkitEntry = isFunction(item.getEntry)
                    ? item.getEntry()
                    : isFunction(item.webkitGetAsEntry) &&
                      item.webkitGetAsEntry();
                  if (webkitEntry && webkitEntry.filesystem) {
                    return walkFileSystem(webkitEntry.filesystem.root).catch(
                      (err) => {
                        if (err === TOO_MANY_FILES) {
                          return [TOO_MANY_FILES];
                        }
                        $log.error('Unable to read directories', err);
                        return [item.getAsFile()];
                      },
                    );
                  } else {
                    return [item.getAsFile()];
                  }
                });
              $q.all(allFiles).then((filesAndDirs) => {
                importFiles([].concat(...filesAndDirs));
              });
            } else if (dt.files) {
              importFiles(Array.from(dt.files));
            }
          }
        }

        /**
         * Walks a file system directory to get a flat list of all nested files with relative paths
         * NOTE: this may stop after 100 files per folder due to browser limitations, but we don't really
         * care, since we don't _really_ want to support that many files
         * @param directory {File}
         * @returns {Promise<Array<File>>}
         */
        function walkFileSystem(directory) {
          let pending = 0;
          let files = [];

          return $q((resolve, reject) => {
            function _readDirectory(directory) {
              pending++;

              const reader = directory.createReader();
              const relativePath = directory.fullPath
                .replace(/^\//, '')
                .replace(/(.+?)\/?$/, '$1/');
              reader.readEntries(function (entries) {
                pending--;
                if (files.length + entries.length > MAX_IMPORT_FILES) {
                  return reject(TOO_MANY_FILES);
                }
                angular.forEach(entries, function (entry) {
                  if (entry.isFile) {
                    pending++;
                    entry.file(function (file) {
                      file.relativePath = relativePath + file.name;
                      files.push(file);
                      if (--pending === 0) {
                        resolve(files);
                      }
                    }, reject);
                  } else {
                    _readDirectory(entry);
                  }
                });

                if (pending === 0) {
                  resolve(files);
                }
              }, reject);
            }

            _readDirectory(directory);
          });
        }

        /**
         * Handles the final import after processing the drop
         * @param files {Array<File>}
         */
        function importFiles(files) {
          if (!files.length || svc.expired) return;

          if (
            files.length > MAX_IMPORT_FILES ||
            files.includes(TOO_MANY_FILES)
          ) {
            appAlert.error(
              `Error attempting to import too many files, please limit importing to no more than ${MAX_IMPORT_FILES} files`,
            );
            return;
          }

          // TODO: expose the following for use in other scenarios
          const gitIgnore = svc.files.find((f) => f.path === '.gitignore');
          const gitIgnoreMatcher =
            gitIgnore && gitIgnoreParser.compile(gitIgnore.contents);
          const ignoredFileChecker = (filePath) => {
            return (
              ALWAYS_EXCLUDE.test(filePath) ||
              (gitIgnoreMatcher && gitIgnoreMatcher.denies(filePath))
            );
          };
          let foundPath = {};
          files = files
            // exclude unsafe files (for now)
            .filter((f) => {
              const fPath = f.relativePath || f.name;
              if (!(languages.validExt(f.name) || safeMimeTypes.test(f.type))) {
                $log.info(
                  `Ignoring dropped file with unknown type: ${f.name} (${f.type})`,
                );
                return false;
              }
              if (ignoredFileChecker(fPath)) {
                $log.info(
                  `Ignoring dropped file due to ignore rules: ${fPath}`,
                );
                return false;
              }
              // filter out duplicates (weird scenarios can cause it)
              return foundPath[fPath] ? false : (foundPath[fPath] = true);
            });

          let dialogResult;
          if (files.length > 1) {
            let examples = files
              .slice(0, files.length > 6 ? 5 : 6)
              .map((f) => f.relativePath || f.name);
            dialogResult = multiFilePathDialog.open({
              title: `Importing ${files.length} files`,
              prompt: 'Where should the imported files be added?',
              placeholder: 'new/path/ (blank is OK)',
              allowRoot: true,
              directory: true,
              showReferenceChoice: !!svc.referenceFiles,
              examples,
              moreFiles: files.length - examples.length,
            });
          } else if (files.length) {
            const f = files[0];
            const fPath = f.relativePath || f.name;
            dialogResult = multiFilePathDialog.open({
              title: `Import ${fPath}`,
              prompt: 'Path for imported file',
              path: fPath,
              showReferenceChoice: !!svc.referenceFiles,
            });
          } else {
            appAlert.warning(
              'Unable to determine that file type by mime or extension.',
            );
          }

          if (dialogResult) {
            let basePath = '';
            let importToReference;
            let importToFiles;
            let extractedReferenceFiles;
            dialogResult
              .then(({ path, isReference, extractReference }) => {
                importToReference = isReference;
                importToFiles = isReference ? svc.referenceFiles : svc.files;
                if (files.length === 1) {
                  // overwrite the single file's path
                  files[0].relativePath = path;
                } else {
                  // set up to overwrite all files
                  basePath = path && path + '/';

                  if (!isReference && extractReference) {
                    extractedReferenceFiles = [];
                    files = files.filter((f) => {
                      const fPath = f.relativePath || f.name;
                      if (/\.ref\./.test(fPath)) {
                        extractedReferenceFiles.push(f);
                        f.relativePath = fPath.replace(/\.ref\./, '.');
                        return false;
                      }
                      return true;
                    });
                  }
                }

                // check for conflicts (this allows us to replace a file on single drag-and-drop)
                return multiFileOverwriteDialog.open({
                  paths: files.map(
                    (f) => basePath + (f.relativePath || f.name),
                  ),
                  files: importToFiles,
                  referencePaths:
                    extractedReferenceFiles &&
                    extractedReferenceFiles.map(
                      (f) => basePath + (f.relativePath || f.name),
                    ),
                  referenceFiles: svc.referenceFiles,
                });
              })
              .then(
                ({
                  paths: includedPaths,
                  referencePaths: includedRefPaths,
                }) => {
                  const importPromises = [];
                  let filesImported = 0;
                  let fileToOpen = false;

                  let stopImporting = false;

                  function importFiles(
                    filesToImport,
                    allowedPaths,
                    filesArray,
                    reference,
                  ) {
                    if (!allowedPaths.length) return;

                    filesToImport.forEach((f) => {
                      const fPath = basePath + (f.relativePath || f.name);
                      if (stopImporting || !allowedPaths.includes(fPath))
                        return;

                      const maxSize = maxSizeFor(f);
                      if (f.size > maxSize) {
                        appAlert.warning(
                          `File "${f.relativePath || f.name}" is too large, please limit files to ${formatSizeLimit(maxSize)}`,
                        );
                        stopImporting = true;
                        return;
                      }
                      const importPromise = getFileContents(f)
                        .catch((err) => {
                          if (stopImporting) return;
                          $log.error('Error loading file from system', err);
                          const errorMsg = err
                            ? ' (' + err.toString() + ')'
                            : '';
                          appAlert.warning(
                            `Unable to read contents for file "${f.relativePath || f.name}"${errorMsg}`,
                          );
                          // create file without contents
                          return '';
                        })
                        .then((contents) => {
                          if (stopImporting) return;
                          const newFile = _createFile(
                            { path: fPath, contents },
                            reference,
                            false,
                          );
                          if (newFile) {
                            fileToOpen = fileToOpen || newFile;
                          } else {
                            stopImporting = true;
                          }
                        });
                      importPromises.push(importPromise);
                    });
                  }

                  importFiles(
                    files,
                    includedPaths,
                    importToFiles,
                    importToReference,
                  );
                  if (extractedReferenceFiles) {
                    importFiles(
                      extractedReferenceFiles,
                      includedRefPaths,
                      svc.referenceFiles,
                      true,
                    );
                  }

                  $q.all(importPromises).then(() => {
                    if (!stopImporting) {
                      if (filesImported > 1) {
                        appAlert.success(
                          `Imported ${filesImported} files successfully`,
                        );
                      } else if (fileToOpen) {
                        svc.clickFile(fileToOpen);
                      }
                      svc.onFileChange({ force: true });
                    }
                  });
                },
              );
          }
        }

        /**
         * Sets up a drop handler on the selected element
         * @param $scope {*?} Angular scope so we can stop listening
         * @param selector {string?} Defaults to '.app', but can be any element we want to listen on (such as a model backdrop)
         */
        svc.setupDrop = ($scope, selector = '.app') => {
          const $app = $document.querySelector(selector);
          $app.on('drop', handleDrop);
          $app.on('dragenter dragover', handleDragOver);
          $app.on('dragexit dragleave dragend', handleDragExit);
          const destroy = () => {
            $app.off('drop', handleDrop);
            $app.off('dragenter dragover', handleDragOver);
            $app.off('dragexit dragleave dragend', handleDragExit);
          };
          svc._mfcListeners.push(destroy);
          if ($scope) {
            $scope.$on('$destroy', destroy);
          }
        };
      }

      return {
        setupDragAndDrop,
      };
    },
  );
